import { Box, Paper, Stack, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useState } from 'react';
import type { Result, OverallRanking, CompetitionPublic } from '../../types/results';
import type { Category, Discipline, PublicDisplayConfig } from '../../types/domain';
import { PodiumResultsView } from './PodiumResultsView';
import { OverallPodiumView } from './OverallPodiumView';

interface SplitViewProps {
  competition: CompetitionPublic;
  config: PublicDisplayConfig;
  results: Result[];
  discipline: Discipline;
  category: Category | null;
  overallForCategory: OverallRanking | undefined;
  highlightTeamId?: number | null;
}

/**
 * 2-sloupcovy rezim: vlevo aktualni disciplina, vpravo celkove poradi.
 * Na mobilu se prepne na Tabs (jeden sloupec viditelny v cas).
 */
export const SplitView = ({
  competition,
  config,
  results,
  discipline,
  category,
  overallForCategory,
  highlightTeamId,
}: SplitViewProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileTab, setMobileTab] = useState<0 | 1>(0);

  const LeftPane = (
    <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
      <Typography
        variant="overline"
        sx={{ fontWeight: 700, letterSpacing: '0.1em', color: 'text.secondary', display: 'block', mb: 1 }}
      >
        {discipline.name} — aktuální disciplína
      </Typography>
      <PodiumResultsView
        results={results}
        config={config}
        showAttempt={(discipline.attemptsCount ?? 1) > 1}
        highlightTeamId={highlightTeamId}
        timeFormat={discipline.timeFormat}
      />
    </Paper>
  );

  const RightPane = (
    <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
      <Typography
        variant="overline"
        sx={{ fontWeight: 700, letterSpacing: '0.1em', color: 'text.secondary', display: 'block', mb: 1 }}
      >
        Celkové pořadí
      </Typography>
      {competition.overallRankingVisible && overallForCategory && category ? (
        <OverallPodiumView
          ranking={overallForCategory}
          disciplines={category.disciplines}
          config={config}
          highlightTeamId={highlightTeamId}
        />
      ) : (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <Typography variant="body2">
            Celkové pořadí se zveřejní po ukončení soutěže.
          </Typography>
        </Box>
      )}
    </Paper>
  );

  if (isMobile) {
    return (
      <Box>
        <Tabs
          value={mobileTab}
          onChange={(_, v) => setMobileTab(v)}
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          <Tab label="Disciplína" />
          <Tab label="Celkové pořadí" />
        </Tabs>
        {mobileTab === 0 ? LeftPane : RightPane}
      </Box>
    );
  }

  return (
    <Stack direction="row" spacing={2} alignItems="stretch" sx={{ minHeight: 400 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>{LeftPane}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>{RightPane}</Box>
    </Stack>
  );
};
